import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import useSWR from 'swr';
import { useState } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';

import { useRouter } from 'src/routes/hooks';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import { ImpersonationBanner } from 'src/components/impersonation-banner';

import { useAuth } from 'src/auth/auth-context';

import { NavMobile } from './nav-mobile';
import { NavVertical } from './nav-vertical';
import { layoutClasses } from '../core/classes';
import { MainSection } from '../core/main-section';
import { MenuButton } from '../components/menu-button';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { fetcher } from 'src/lib/axios';
import { Label } from 'src/components/label';

import { navData as dashboardNavData, filterNavByTipo } from '../nav-config-dashboard';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';

import type { MainSectionProps } from '../core/main-section';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';

// ----------------------------------------------------------------------
//
// Layout ENXUTO do Portal (scaffold). Reescrito a partir do
// frontend/src/layouts/dashboard/layout.tsx, removendo o acoplamento do
// Auditoria: sem permissões (usePermissions/useMenuPermissions), sem
// account-drawer/searchbar/settings-button, sem nav horizontal. Mantém o VISUAL
// Minimal (mesmas primitivas: LayoutSection + HeaderSection + NavVertical).
// O próximo agente pluga aqui o usuário logado real e o logout de verdade.
// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: { data?: NavSectionProps['data'] };
    main?: MainSectionProps;
  };
};

// ----------------------------------------------------------------------

function AccountMenu() {
  const router = useRouter();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    router.push('/login');
  };

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
        <Avatar sx={{ width: 36, height: 36 }}>
          <Iconify icon="solar:user-rounded-bold" width={20} />
        </Avatar>
      </IconButton>

      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 200 } } }}
      >
        {user && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <ListItemText
              primary="Contribuinte"
              secondary={user.usr_login}
              primaryTypographyProps={{ typography: 'caption', color: 'text.secondary' }}
              secondaryTypographyProps={{ typography: 'subtitle2', color: 'text.primary' }}
            />
          </Box>
        )}
        <MenuItem onClick={() => router.push('/perfil')}>
          <ListItemText primary="Meu Perfil" />
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemText primary="Sair" />
        </MenuItem>
      </Menu>
    </>
  );
}

// ----------------------------------------------------------------------

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
}: DashboardLayoutProps) {
  const theme = useTheme();
  const settings = useSettingsContext();

  // Gate por tipo de contribuinte: PF vê "Serviços Tomados"; PJ vê a seção
  // "Contribuinte Externo". Enquanto o tipo não carrega, filterNavByTipo assume
  // PF (esconde Externo) p/ não piscar itens PJ-only. Cacheado pelo SWR.
  const { data: tipoData } = useSWR<{ is_pj: boolean }>('/portal-api/usuario/tipo', fetcher);
  // Badge de não-lidas no menu "Notificações" (DTE). Poll leve (30s).
  const { data: dteData } = useSWR<{ naoLidas: number }>('/portal-api/dte', fetcher, {
    refreshInterval: 30000,
  });
  const naoLidas = dteData?.naoLidas ?? 0;
  const baseNav = slotProps?.nav?.data ?? filterNavByTipo(dashboardNavData, tipoData?.is_pj);
  // Injeta o contador no item /notificacoes sem mutar o navData original.
  const navData = baseNav.map((sec) => ({
    ...sec,
    items: sec.items.map((it) =>
      it.path === '/notificacoes' && naoLidas > 0
        ? { ...it, info: <Label color="error">{naoLidas}</Label> }
        : it
    ),
  }));
  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);

  const isNavMini = settings.state.navLayout === 'mini';

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const renderHeader = () => {
    const headerSlots: HeaderSectionProps['slots'] = {
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile data={navData} open={open} onClose={onClose} cssVars={navVars.section} />

          {/** @slot Logo (mobile) */}
          <Logo
            sx={{
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
            }}
          />
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          <AccountMenu />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={{
          container: { maxWidth: false, sx: { px: { [layoutQuery]: 5 } } },
          ...slotProps?.header?.slotProps,
        }}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={() =>
        settings.setField(
          'navLayout',
          settings.state.navLayout === 'vertical' ? 'mini' : 'vertical'
        )
      }
    />
  );

  const renderMain = () => (
    <MainSection {...slotProps?.main}>
      <ImpersonationBanner />
      {children}
    </MainSection>
  );

  return (
    <LayoutSection
      headerSection={renderHeader()}
      sidebarSection={renderSidebar()}
      footerSection={null}
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
